import { SerializedError, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { TProductInstance } from '@lib/core/products/types';
import { getProductsListData } from '@lib/core/products/utils';
import { ApiQueries, ApiValues } from '@lib/core/service/consts';
import { IFetchProductsOptions, IProductsRequestMeta, IProductsResponse } from '@lib/core/service/types/interface';
import { shuffleProductsInsideRankGroups } from '@lib/core/service/utils';

export interface IProductsRequestData {
  count: number | undefined;
  next: string;
  previous: string;
  results: TProductInstance[];
}
export interface IProductCatalogState {
  isProductsRequestLoading: boolean;
  isFeaturedProductsLoading: boolean;
  productsRequestError: SerializedError;
  productsData: IProductsRequestData;
  lastDataLoadedTime: number;
  lastFeaturedProductsLoadedTime: number;
  featuredProducts: TProductInstance[];
}

export const initialState: IProductCatalogState = {
  featuredProducts: [],
  isFeaturedProductsLoading: false,
  isProductsRequestLoading: false,
  lastDataLoadedTime: 0,
  lastFeaturedProductsLoadedTime: 0,
  productsData: {
    count: undefined,
    next: '',
    previous: '',
    results: [],
  },
  productsRequestError: null,
};

export const actionFetchProducts = createAsyncThunk(
  'actionFetchProducts',
  async ({ params, shouldShuffleResponse = false }: IFetchProductsOptions) => {
    const response = await getProductsListData({ ...params });
    return {
      ...response,
      results:
        shouldShuffleResponse || !params[ApiQueries.productName]
          ? shuffleProductsInsideRankGroups(response.results)
          : response.results,
    };
  },
);

export const actionFetchProductsListsFeatured = createAsyncThunk(
  // ToDo genetic approach on catalog v2
  'actionFetchProductsListsFeatured',
  async ({ params, shouldShuffleResponse }: IFetchProductsOptions) => {
    const response = await getProductsListData({
      ...params,
      [ApiQueries.promotions]: ApiValues.promotionFeature,
    });
    return {
      ...response,
      results: shouldShuffleResponse ? shuffleProductsInsideRankGroups(response.results) : response.results,
    };
  },
);

export const searchCatalogSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(
      actionFetchProducts.pending,
      (state, action: { meta: IProductsRequestMeta; payload: IProductsResponse }) => {
        const { meta: { arg: { isPagination = false, isJournalCase = false } = {} } = {} } = action;
        if (!isPagination && !isJournalCase) {
          state.productsData = {
            count: undefined,
            next: '',
            previous: '',
            results: [],
          };
        }
        state.isProductsRequestLoading = true;
      },
    );
    builder.addCase(
      actionFetchProducts.fulfilled,
      (state, action: { meta: IProductsRequestMeta; payload: IProductsResponse }) => {
        const { payload: { results = [] } = {}, meta: { arg: { shouldShuffleResponse, isPagination } = {} } = {} } =
          action;
        const newArray = shouldShuffleResponse ? shuffleProductsInsideRankGroups(results) : results;
        state.productsData = {
          ...action.payload,
          results: isPagination ? [...state.productsData.results, ...newArray] : newArray,
        };
        state.isProductsRequestLoading = false;
        state.lastDataLoadedTime = new Date().getTime();
      },
    );
    builder.addCase(actionFetchProductsListsFeatured.pending, state => {
      state.isFeaturedProductsLoading = true;
    });
    builder.addCase(actionFetchProductsListsFeatured.rejected, state => {
      state.isFeaturedProductsLoading = false;
    });
    builder.addCase(
      actionFetchProductsListsFeatured.fulfilled,
      (state, action: { meta: IProductsRequestMeta; payload: IProductsResponse }) => {
        const { payload: { results = [] } = {} } = action;
        state.isFeaturedProductsLoading = false;
        state.featuredProducts = results;
        state.lastFeaturedProductsLoadedTime = new Date().getTime();
      },
    );
    builder.addCase(actionFetchProducts.rejected, (state, action) => {
      state.productsRequestError = action.error;
      state.isProductsRequestLoading = false;
    });
  },
  initialState,
  name: 'searchCatalog',
  reducers: {
    resetSearchCatalog: () => initialState,
  },
});

export default searchCatalogSlice.reducer;
export const { resetSearchCatalog } = searchCatalogSlice.actions;
